import { inject, observer } from 'mobx-react';
import { Link as RouterLink } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { Layer } from 'bernie-view';

import { RoomViewModel } from 'models';

import { UitkLink } from 'uitk-react-link';

import { GalleryDialog } from 'components/infosite/property-details/gallery-dialog';
import { RoomInfoCrawlable } from './room-info-crawlable';
import { RoomInfoDialog } from './room-info-dialog';

const LinkTracked = ClickTracker(UitkLink);
const noop = () => undefined;

export interface RoomInfoProps {
  analytics?: AnalyticsStore;
  className?: string;
  dialogPrefix?: string;
  position: string;
  room: RoomViewModel;
  routeStore?: RouteStore;
  trackingRfrr?: string;
  width?: string;
  includeGallery?: boolean;
  excludeReserveButton?: boolean;
  isDateless?: boolean;
  excludePriceBreakDownLink?: boolean;
}

export interface RoomInfoState {
  initialized: boolean;
}

@inject('routeStore', 'analytics')
@observer
export class RoomInfo extends React.Component<RoomInfoProps, RoomInfoState> {
  private roomInfoWrapperRef;

  public static defaultProps: Partial<RoomInfoProps> = {
    dialogPrefix: '',
    includeGallery: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      initialized: false,
    };

    this.roomInfoDialogCloseClickCallback = this.roomInfoDialogCloseClickCallback.bind(this);
    this.onRoomInfoDialogClick = this.onRoomInfoDialogClick.bind(this);
    this.galleryDialogCloseCallback = this.galleryDialogCloseCallback.bind(this);
  }

  public componentDidMount() {
    this.setState({
      initialized: true,
    });
  }

  private onRoomInfoDialogClick() {
    if (this.props.room.shouldTrack3piDialog) {
      const trackingDataFor3piDialogClick = {
        rfrr: 'RoomsAndRates.ShowRoomInformation.3PI',
        linkName: 'Open Room Info dialog 3PI',
      };
      this.props.analytics.trackEvent(trackingDataFor3piDialogClick.rfrr, trackingDataFor3piDialogClick.linkName, null, null);
    }
  }

  private roomInfoDialogCloseClickCallback() {
    const focus = this.roomInfoWrapperRef?.focus;
    this.galleryDialogCloseCallback();
    if (typeof focus === 'function') this.roomInfoWrapperRef.focus();
  }

  private galleryDialogCloseCallback() {
    const goBack = this.props.routeStore?.goBack;
    if (typeof goBack === 'function') this.props.routeStore.goBack();
  }

  public render() {
    const { initialized } = this.state;
    const {
      analytics,
      className,
      children,
      dialogPrefix,
      excludeReserveButton,
      position,
      room,
      routeStore,
      trackingRfrr,
      width,
      includeGallery,
      isDateless,
      excludePriceBreakDownLink,
    } = this.props;
    const dialogKey = `${dialogPrefix}roomInfo-${room.roomTypeCode}`;
    const query = routeStore.mergeQueryString(routeStore.search, { pwaDialog: dialogKey });
    const param = { pwaDialog: `photoGallery-roomGallery${position}` };

    const roomInfoWrapperRef = ref => {
      this.roomInfoWrapperRef = ref;
    };

    return (
      <>
        {initialized && (
          <React.Fragment>
            <LinkTracked
              innerRef={roomInfoWrapperRef}
              className={`uitk-flex room-rate-dialog-trigger__btn uitk-type-left ${className}`}
              inline={true}
              linkName="Open Room Info dialog"
              onClick={this.props.room.shouldTrack3piDialog ? this.onRoomInfoDialogClick : null}
              padding="standard"
              rfrr={trackingRfrr}
              role="button"
              tag={UitkLink}
              width={width}
              data-stid={this.props['data-stid']}
            >
              <RouterLink to={{ pathname: routeStore.pathname, search: query }}>{children}</RouterLink>
            </LinkTracked>
            {isDateless && <RoomInfoCrawlable room={room} />}
          </React.Fragment>
        )}
        {!initialized && children}
        <QueryRoute query={{ pwaDialog: dialogKey }}>
          <Layer id="infosite-roominfo" onDismiss={this.roomInfoDialogCloseClickCallback}>
            <RoomInfoDialog
              action={this.roomInfoDialogCloseClickCallback}
              analytics={analytics}
              position={position}
              room={room}
              trackingRfrr={trackingRfrr}
              excludeReserveButton={excludeReserveButton}
              routeStore={routeStore}
              excludePriceBreakDownLink={excludePriceBreakDownLink}
            />
          </Layer>
        </QueryRoute>
        {includeGallery && (
          <QueryRoute query={param}>
            <Layer id="infosite-gallery">
              <GalleryDialog
                currentIndex={0}
                galleryDialogCloseCallback={this.galleryDialogCloseCallback}
                onIndexChange={noop}
                galleryName="Dialog"
                images={room.photos}
                trackingRfrr={trackingRfrr}
              />
            </Layer>
          </QueryRoute>
        )}
      </>
    );
  }
}
